import { useEffect, useMemo, useState, type ComponentType } from "react";
import type { Email } from "../../utils/interfaces";
import type { ReplyMode, ReplySelfContext } from "../../utils/replyRecipients";
import { EmailMessageBubble } from "./EmailMessageBubble";
import { isOutgoingEmail } from "./emailDirection";
import { parseApiDateTime } from "../../utils/date";

type EmailMessageBubbleProps = {
  email: Email;
  isOutgoing: boolean;
  onReply?: (message: Email, mode: ReplyMode) => void;
  highlighted?: boolean;
};

function sortEmailsChronologically(emails: Email[]): Email[] {
  return [...emails].sort(
    (a, b) => parseApiDateTime(a.when).getTime() - parseApiDateTime(b.when).getTime(),
  );
}

export { sortEmailsChronologically };

export function EmailConversationMessages({
  emails,
  mailboxContext,
  onReply,
  collapseThreshold = 3,
  highlightMessageId,
  MessageBubble = EmailMessageBubble,
}: {
  emails: Email[];
  mailboxContext: ReplySelfContext;
  onReply?: (message: Email, mode: ReplyMode) => void;
  collapseThreshold?: number;
  highlightMessageId?: string | null;
  MessageBubble?: ComponentType<EmailMessageBubbleProps>;
}) {
  const [expandedOlder, setExpandedOlder] = useState(false);

  const sortedEmails = useMemo(
    () => sortEmailsChronologically(emails),
    [emails],
  );

  const highlightInCollapsed =
    highlightMessageId != null &&
    sortedEmails.some(
      (email, index) =>
        email.gmail_message_id === highlightMessageId &&
        index < sortedEmails.length - collapseThreshold,
    );

  useEffect(() => {
    if (highlightInCollapsed) {
      setExpandedOlder(true);
    }
  }, [highlightInCollapsed]);

  useEffect(() => {
    if (!highlightMessageId) {
      return;
    }

    const element = document.getElementById(`message-${highlightMessageId}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightMessageId, expandedOlder, sortedEmails]);

  const hiddenCount = Math.max(0, sortedEmails.length - collapseThreshold);
  const visibleEmails =
    hiddenCount > 0 && !expandedOlder
      ? sortedEmails.slice(-collapseThreshold)
      : sortedEmails;

  if (sortedEmails.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-slate-500">
        No messages in this conversation
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {hiddenCount > 0 && !expandedOlder ? (
        <button
          type="button"
          onClick={() => setExpandedOlder(true)}
          className="w-full rounded-md border border-slate-200 bg-white py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
        >
          {hiddenCount} older message{hiddenCount === 1 ? "" : "s"}
        </button>
      ) : null}
      {visibleEmails.map((email, index) => (
        <MessageBubble
          key={email.gmail_message_id ?? `${email.when}-${index}`}
          email={email}
          isOutgoing={isOutgoingEmail(email, mailboxContext)}
          onReply={onReply}
          highlighted={
            highlightMessageId != null &&
            email.gmail_message_id === highlightMessageId
          }
        />
      ))}
    </div>
  );
}
